import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, Trophy, MessageCircle, Users, Calendar, Activity, 
  TrendingUp, Code2, Star, ArrowUpRight, ArrowDownRight, Minus,
  Hash, Clock
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { formatLastUpdated, formatSiteAddedDate, lastUpdatedSource } from '../utils/formatLastUpdated';
import ForumRating from './ForumRating';
import Breadcrumbs from './Breadcrumbs';
import RelatedForums from './RelatedForums';
import ForumCategoryRank from './ForumCategoryRank';
import SponsoredBadge from './SponsoredBadge';
import BackToTop from './BackToTop';
import { resetMetaTags, removeAllJsonLd } from '../utils/meta';
import { applyForumPageSeo } from '../utils/forumSeo';
import { generateForumDescription } from '../utils/forumDescriptions';
import { statTrend, formatStatDeltaSigned, type StatTrend } from '../utils/forumStatChange';
import { calculateForumScore } from '../utils/forumScore';
import { createCategorySlug, createSoftwareSlug } from '../utils/urlHelpers';
import ForumHistoryChart from './ForumHistoryChart';
import WatchlistButton from './WatchlistButton';
import ForumScoreExplainer from './ForumScoreExplainer';
import { forumSiteAgeYears } from '../utils/forumGrowth';
import CategoryBenchmark from './CategoryBenchmark';
import ForumEmbedCard from './ForumEmbedCard';
import { getCategoryPeers } from '../utils/forumRank';
import ActivityHeatmap from './ActivityHeatmap';
import DataReportButton from './DataReportButton';
import HelpfulFeedback from './HelpfulFeedback';
import ShareForumCard from './ShareForumCard';
import VerifiedBadge from './VerifiedBadge';
import ForumRiskBadge from './ForumRiskBadge';
import WaybackLink from './WaybackLink';
import ForumClaimForm from './ForumClaimForm';
import SoftwareMigrationHistory from './SoftwareMigrationHistory';
import { trackSponsoredClick } from '../services/featureDb';
import { appendOutboundUtm } from '../utils/outboundUrl';
import { isSponsorActive } from '../utils/sponsor';
import type { ForumBase } from '../types/forum';
import { FORUM_PUBLIC_SELECT } from '../utils/forumSelect';
import { withCacheBust } from '../utils/mediaUrl';

interface Forum {
  id: string;
  forum_id: string;
  name: string;
  url: string;
  category: string;
  topics_count: number;
  posts_count: number;
  members_count: number;
  founded_year: number;
  description: string;
  logo_url: string;
  is_active: boolean;
  software: string | null;
  average_rating: number;
  rating_count: number;
  is_sponsored: boolean;
  sponsored_until: string | null;
  previous_month_topics_count: number;
  previous_month_posts_count: number;
  previous_month_members_count: number;
  updated_at?: string | null;
  created_at?: string | null;
  is_verified?: boolean;
  wayback_url?: string | null;
  tags?: string[];
}

interface Props {
  darkMode: boolean;
}

type ForumTab = 'overview' | 'history' | 'category' | 'ranking' | 'similar' | 'embed' | 'feedback';

const FORUM_TABS: { id: ForumTab; label: string }[] = [
  { id: 'overview', label: 'Özet' },
  { id: 'history', label: 'İstatistik geçmişi' },
  { id: 'category', label: 'Kategori' },
  { id: 'ranking', label: 'Sıralama' },
  { id: 'similar', label: 'Benzer forumlar' },
  { id: 'embed', label: 'Sitene ekle' },
  { id: 'feedback', label: 'Geri bildirim' },
];

const ForumDetails: React.FC<Props> = ({ darkMode }) => {
  const { forumId } = useParams<{ forumId: string }>();
  const [forum, setForum] = useState<Forum | null>(null);
  const [allForums, setAllForums] = useState<Forum[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<ForumTab>('overview');

  useEffect(() => {
    fetchForumDetails();
  }, [forumId]);

  useEffect(() => {
    if (forum) {
      applyForumPageSeo(forum, allForums as ForumBase[]);
    }

    return () => {
      resetMetaTags();
      removeAllJsonLd();
    };
  }, [forum, allForums]);

  const fetchForumDetails = async () => {
    if (!forumId) {
      setLoading(false);
      return;
    }

    const slug = decodeURIComponent(forumId);
    setLoading(true);
    setForum(null);

    try {
      const { data, error } = await supabase
        .from('forums')
        .select(FORUM_PUBLIC_SELECT)
        .eq('forum_id', slug)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        setForum(null);
        return;
      }

      setForum(data as Forum);

      const { data: allForumsData, error: allForumsError } = await supabase
        .from('forums')
        .select(FORUM_PUBLIC_SELECT)
        .eq('is_active', true);

      if (!allForumsError) {
        setAllForums((allForumsData || []) as Forum[]);
      }
    } catch (error) {
      console.error('Error fetching forum details:', error);
      setForum(null);
    } finally {
      setLoading(false);
    }
  };

  const calculateScore = (f: Forum): number =>
    calculateForumScore(f.topics_count, f.posts_count, f.members_count, f.founded_year);

  const calculatePreviousScore = (f: Forum): number =>
    calculateForumScore(
      f.previous_month_topics_count,
      f.previous_month_posts_count,
      f.previous_month_members_count,
      f.founded_year
    );

  const getRanking = (forum: Forum, field: keyof Forum | 'score'): number => {
    const sortedForums = [...allForums].sort((a, b) => {
      if (field === 'score') {
        return calculateScore(b) - calculateScore(a);
      }
      return ((b[field] as number) ?? 0) - ((a[field] as number) ?? 0);
    });
    return sortedForums.findIndex(f => f.id === forum.id) + 1;
  };

  // Artış yeşil, düşüş kırmızı, sabit mavi (0), baz yoksa gri (—).
  const trendTextClass = (t: StatTrend): string => {
    if (t === 'up') return 'text-green-500';
    if (t === 'down') return 'text-red-500';
    if (t === 'flat') return 'text-blue-500';
    return darkMode ? 'text-gray-400' : 'text-gray-500';
  };

  const trendIcon = (t: StatTrend) => {
    if (t === 'up') return <ArrowUpRight size={16} className="text-green-500" />;
    if (t === 'down') return <ArrowDownRight size={16} className="text-red-500" />;
    if (t === 'flat') return <Minus size={16} className="text-blue-500" />;
    return <Minus size={16} className="text-gray-400" />;
  };

  const renderDelta = (current: number, previous: number | null | undefined) => {
    const t = statTrend(current, previous);
    const text = formatStatDeltaSigned(current, previous) ?? '—';
    return (
      <div className="flex items-center gap-1 text-[11px] sm:text-xs">
        {trendIcon(t)}
        <span className={`tabular-nums ${trendTextClass(t)}`}>{text}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!forum) {
    return (
      <div className="text-center py-12">
        <p className={`text-xl ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Forum bulunamadı</p>
        <Link to="/" className="text-indigo-500 hover:text-indigo-600 mt-4 inline-block">
          Ana sayfaya dön
        </Link>
      </div>
    );
  }

  const updatedLabel = formatLastUpdated(lastUpdatedSource(forum));
  const addedToSiteLabel = formatSiteAddedDate(forum.created_at);
  const postsPerMember =
    forum.members_count > 0 ? (forum.posts_count / forum.members_count).toFixed(2) : null;
  const postsPerTopic =
    forum.topics_count > 0 ? (forum.posts_count / forum.topics_count).toFixed(2) : null;
  const categoryPeers = getCategoryPeers(allForums as ForumBase[], forum.category);
  const showCategoryBenchmark = categoryPeers.length >= 2;

  const tabEmpty = (msg: string) => (
    <div
      className={`rounded-xl border p-6 text-center text-sm ${
        darkMode ? 'bg-gray-700/40 border-gray-600 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-600'
      }`}
    >
      {msg}
    </div>
  );

  return (
    <div className={`min-h-screen overflow-x-hidden ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-7xl">
        <Breadcrumbs darkMode={darkMode} skipJsonLd />
        <Link
          to="/"
          className={`inline-flex items-center gap-2 mb-6 ${
            darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ArrowLeft size={20} />
          Geri Dön
        </Link>

        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 sm:p-6 lg:p-8 shadow-lg relative overflow-hidden`}>
          {isSponsorActive(forum) && (
            <div className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10">
              <SponsoredBadge
                forum={forum}
                darkMode={darkMode}
                onTrackClick={() => trackSponsoredClick(forum.id)}
              />
            </div>
          )}

          <div className="flex flex-col items-center gap-4 sm:gap-6 mb-6 sm:mb-8">
            {forum.logo_url && (
              <img
                src={withCacheBust(forum.logo_url, forum.updated_at) ?? forum.logo_url}
                alt={forum.name}
                className="w-32 h-32 sm:w-48 sm:h-48 lg:w-64 lg:h-64 object-contain rounded-lg"
              />
            )}
            <div className="text-center">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2 px-2 flex flex-wrap items-center justify-center gap-2">
                {forum.name}
                {forum.is_verified && <VerifiedBadge />}
                <ForumRiskBadge forum={forum as ForumBase} />
              </h1>
              <div className="flex flex-wrap justify-center gap-2 mb-3">
                <WatchlistButton forumId={forum.id} darkMode={darkMode} />
                <ShareForumCard forumId={forum.forum_id} name={forum.name} darkMode={darkMode} />
                <Link
                  to={`/compare?a=${forum.forum_id}`}
                  className={`px-3 py-1.5 rounded-lg text-sm ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}
                >
                  Karşılaştırmaya ekle
                </Link>
              </div>
              <p className={`text-sm sm:text-base lg:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-4 px-2`}>
                {forum.description ||
                  generateForumDescription(
                    forum.forum_id,
                    forum.name,
                    forum.category,
                    forum.members_count,
                    forum.posts_count,
                    forum.topics_count
                  )}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 px-2">
                <Link 
                  to={`/category/${createCategorySlug(forum.category)}`}
                  className={`px-2 py-1 sm:px-3 rounded-full text-xs sm:text-sm ${
                    darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                  } transition-colors`}
                >
                  {forum.category}
                </Link>
                {forum.software && (
                  <Link
                    to={`/software/${createSoftwareSlug(forum.software)}`}
                    className={`flex items-center gap-1 sm:gap-2 px-2 py-1 sm:px-3 rounded-full text-xs sm:text-sm ${
                      darkMode 
                        ? 'bg-gray-700 text-indigo-400 hover:bg-gray-600' 
                        : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
                    } transition-colors`}
                  >
                    <Code2 size={16} />
                    {forum.software}
                  </Link>
                )}
                <span className={`flex items-center gap-1 text-xs sm:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <Calendar size={16} />
                  {forum.founded_year}
                </span>
                {forum.url && (
                  <a
                    href={
                      appendOutboundUtm(forum.url) ?? forum.url
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-1 px-2 py-1 sm:px-3 rounded-full text-xs sm:text-sm transition-colors ${
                      darkMode
                        ? 'bg-gray-700 text-indigo-400 hover:bg-gray-600'
                        : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
                    }`}
                  >
                    Ziyaret Et
                  </a>
                )}
                {(!forum.is_active || forum.wayback_url) && (
                  <WaybackLink
                    url={forum.url}
                    waybackUrl={forum.wayback_url}
                    name={forum.name}
                    darkMode={darkMode}
                    pill
                  />
                )}
              </div>
            </div>
          </div>

          <div
            className={`flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm mb-5 pb-4 border-b ${
              darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'
            }`}
          >
            <span className="flex items-center gap-1.5" title={forum.created_at || undefined}>
              <Calendar size={16} />
              Siteye eklenme:{' '}
              <span className={forum.created_at ? '' : 'italic opacity-80'}>
                {addedToSiteLabel}
              </span>
            </span>
            <span className={`hidden sm:inline ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>·</span>
            <span className="flex items-center gap-1.5">
              <Clock size={16} />
              Son güncelleme: {updatedLabel}
            </span>
          </div>

          <div
            className={`flex flex-wrap gap-1 p-1 rounded-lg mb-6 ${
              darkMode ? 'bg-gray-700/80' : 'bg-gray-100'
            }`}
            role="tablist"
          >
            {FORUM_TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={activeTab === t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === t.id
                    ? darkMode
                      ? 'bg-gray-600 text-white shadow-sm'
                      : 'bg-white text-gray-900 shadow-sm'
                    : darkMode
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === 'history' && (
            <ForumHistoryChart
              forum={forum}
              darkMode={darkMode}
              refreshKey={forum.updated_at ? new Date(forum.updated_at).getTime() : 0}
            />
          )}

          {activeTab === 'overview' && (
            <>
          <ForumScoreExplainer forum={forum} darkMode={darkMode} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-6">
            <div
              className={`p-4 rounded-lg flex items-center gap-3 ${
                darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-50 text-gray-800'
              }`}
            >
              <Calendar size={22} className="text-sky-500 shrink-0" />
              <div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Site yaşı</p>
                <p className="text-lg font-semibold tabular-nums">{forumSiteAgeYears(forum)} yıl</p>
              </div>
            </div>
            <div
              className={`p-4 rounded-lg flex items-center gap-3 ${
                darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-50 text-gray-800'
              }`}
            >
              <TrendingUp size={22} className="text-indigo-500 shrink-0" />
              <div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Üye başına ileti</p>
                <p className="text-lg font-semibold tabular-nums">
                  {postsPerMember != null ? `${postsPerMember} ileti/üye` : '—'}
                </p>
              </div>
            </div>
            <div
              className={`p-4 rounded-lg flex items-center gap-3 ${
                darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-50 text-gray-800'
              }`}
            >
              <Activity size={22} className="text-green-500 shrink-0" />
              <div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Konu başına ileti</p>
                <p className="text-lg font-semibold tabular-nums">
                  {postsPerTopic != null ? `${postsPerTopic} ileti/konu` : '—'}
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* Forum Score Card */}
            <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} p-3 sm:p-4 rounded-lg`}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <Trophy className="text-amber-500 shrink-0" size={18} />
                  <h3 className="text-sm sm:text-base font-semibold truncate">Skor</h3>
                </div>
                {renderDelta(calculateScore(forum), calculatePreviousScore(forum))}
              </div>
              <div className="flex items-end justify-between gap-2">
                <span className="text-lg sm:text-xl font-bold tabular-nums">
                  {calculateScore(forum).toLocaleString('tr-TR')}
                </span>
                <span className={`text-[11px] sm:text-xs tabular-nums ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  #{getRanking(forum, 'score')}
                </span>
              </div>
              <div className={`mt-1 text-[11px] sm:text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Genel sıralama
              </div>
            </div>

            {/* Topics Card */}
            <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} p-3 sm:p-4 rounded-lg`}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <MessageCircle className="text-purple-500 shrink-0" size={18} />
                  <h3 className="text-sm sm:text-base font-semibold truncate">Konu</h3>
                </div>
                {renderDelta(forum.topics_count, forum.previous_month_topics_count)}
              </div>
              <div className="flex items-end justify-between gap-2">
                <span className="text-lg sm:text-xl font-bold tabular-nums">{forum.topics_count.toLocaleString('tr-TR')}</span>
                <span className={`text-[11px] sm:text-xs tabular-nums ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  #{getRanking(forum, 'topics_count')}
                </span>
              </div>
              <div className={`mt-1 text-[11px] sm:text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Sıralama</div>
            </div>

            {/* Posts Card */}
            <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} p-3 sm:p-4 rounded-lg`}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <Activity className="text-green-500 shrink-0" size={18} />
                  <h3 className="text-sm sm:text-base font-semibold truncate">İleti</h3>
                </div>
                {renderDelta(forum.posts_count, forum.previous_month_posts_count)}
              </div>
              <div className="flex items-end justify-between gap-2">
                <span className="text-lg sm:text-xl font-bold tabular-nums">{forum.posts_count.toLocaleString('tr-TR')}</span>
                <span className={`text-[11px] sm:text-xs tabular-nums ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  #{getRanking(forum, 'posts_count')}
                </span>
              </div>
              <div className={`mt-1 text-[11px] sm:text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Sıralama</div>
            </div>

            {/* Members Card */}
            <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} p-3 sm:p-4 rounded-lg`}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <Users className="text-blue-500 shrink-0" size={18} />
                  <h3 className="text-sm sm:text-base font-semibold truncate">Üye</h3>
                </div>
                {renderDelta(forum.members_count, forum.previous_month_members_count)}
              </div>
              <div className="flex items-end justify-between gap-2">
                <span className="text-lg sm:text-xl font-bold tabular-nums">{forum.members_count.toLocaleString('tr-TR')}</span>
                <span className={`text-[11px] sm:text-xs tabular-nums ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  #{getRanking(forum, 'members_count')}
                </span>
              </div>
              <div className={`mt-1 text-[11px] sm:text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Sıralama</div>
            </div>
          </div>

          <div
            className={`mt-6 rounded-xl border p-4 sm:p-6 ${
              darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-amber-50/80 border-amber-100'
            }`}
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="min-w-0">
                <h3
                  className={`flex items-center gap-2 text-base sm:text-lg font-semibold ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  <Star size={20} className="text-amber-500 fill-amber-500/30" />
                  Topluluk değerlendirmesi
                </h3>
                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {forum.rating_count > 0
                    ? `Ortalama ${forum.average_rating?.toFixed(1)} · ${forum.rating_count} oy`
                    : 'Henüz topluluk oyu yok — ilk puanı siz verin'}
                </p>
                {!forum.is_active && (
                  <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    Kapanmış forumlar için puanlar arşiv amaçlıdır.
                  </p>
                )}
              </div>
              <ForumRating
                forumId={forum.id}
                initialRating={forum.average_rating}
                totalRatings={forum.rating_count}
                darkMode={darkMode}
              />
            </div>
          </div>

          <div className="mt-6">
            <ActivityHeatmap forumUuid={forum.id} darkMode={darkMode} />
          </div>
          <SoftwareMigrationHistory forumUuid={forum.id} darkMode={darkMode} />
            </>
          )}

          {activeTab === 'category' && (
            showCategoryBenchmark ? (
              <CategoryBenchmark
                forum={forum as ForumBase}
                allForums={allForums as ForumBase[]}
                darkMode={darkMode}
              />
            ) : (
              tabEmpty('Bu kategoride kıyaslama için yeterli forum bulunmuyor.')
            )
          )}

          {activeTab === 'ranking' && (
            showCategoryBenchmark ? (
              <ForumCategoryRank
                forum={forum as ForumBase}
                allForums={allForums as ForumBase[]}
                darkMode={darkMode}
                className="!mt-0"
              />
            ) : (
              tabEmpty('Bu kategoride sıralama için yeterli forum bulunmuyor.')
            )
          )}

          {activeTab === 'similar' && (
            allForums.length > 0 ? (
              <RelatedForums currentForum={forum as ForumBase} darkMode={darkMode} />
            ) : (
              tabEmpty('Benzer forum bulunamadı.')
            )
          )}

          {activeTab === 'embed' && (
            <ForumEmbedCard forumId={forum.forum_id} forumName={forum.name} darkMode={darkMode} />
          )}

          {activeTab === 'feedback' && (
            <div
              className={`rounded-xl border p-4 sm:p-5 flex flex-col gap-4 ${
                darkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <h3 className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                Geri bildirim
              </h3>
              <HelpfulFeedback forumId={forum.id} darkMode={darkMode} />
              <DataReportButton forumId={forum.id} darkMode={darkMode} />
              <ForumClaimForm forumId={forum.id} darkMode={darkMode} />
            </div>
          )}
        </div>
      </div>
      
      <BackToTop darkMode={darkMode} />
    </div>
  );
};

export default ForumDetails;